import React, { useMemo } from 'react';
import { Box, GridItem, VStack } from '@chakra-ui/react';
import PropTypes from 'prop-types';

import { CypressIds } from '../../../../../../../../../../../../constants/cypressIds';
import { useActiveRecordDocuments, useRecordDocumentRequirements } from '../../../../../../../../../../../../services/record-service';

import EmptyCell from './cells/EmptyCell';
import ExpirationDateCell from './cells/ExpirationDateCell';
import FileNameCell from './cells/FileNameCell';
import RequirementTypeCell from './cells/RequirementTypeCell';
import StatusCell from './cells/StatusCell';
import UploadDateCell from './cells/UploadDateCell';
import Row from './components/Row';
import { mergeDocumentRequirementsWithDocuments } from './util';

const DocumentsTable = ({ insuredId, recordId, jobId }) => {
  const { data: documentRequirements, isLoading: docReqsLoading } = useRecordDocumentRequirements(recordId);
  const { data: documents, isLoading: docsLoading } = useActiveRecordDocuments({ recordId });

  const mergedDocumentRequirements = useMemo(() => {
    if (docReqsLoading || docsLoading) {
      return [];
    }

    return mergeDocumentRequirementsWithDocuments(documentRequirements, documents);
  }, [documentRequirements, documents]);

  return (
    <VStack data-cy={CypressIds.documentRequirementSection} width="100%">
      <Row bgColor="greyScale.100" py={3} textStyle="bodySm600">
        <GridItem alignSelf="center" colSpan={3} justifySelf="center">Required Document Type</GridItem>
        <GridItem alignSelf="center" colSpan={1} justifySelf="center">Expiration Date</GridItem>
        <GridItem alignSelf="center" colSpan={3} justifySelf="center">File Name</GridItem>
        <GridItem alignSelf="center" colSpan={1} justifySelf="center">Upload Date</GridItem>
        <GridItem alignSelf="center" colSpan={2} justifySelf="center">Status</GridItem>
      </Row>
      {mergedDocumentRequirements.map(requirement => (
        <Box
          _even={{ bg: 'white' }}
          _odd={{ bg: 'greyScale.100' }}
          data-cy={CypressIds.docTypeRow(requirement.insuranceDocumentTypeId)}
          key={requirement.documents[0]?.id}
          marginTop="0 !important"
          maxHeight="400px"
          overflowY="auto"
          width="100%"
        >
          <Row sticky data-cy={CypressIds.documentRow(requirement.documents[0]?.id)}>
            <RequirementTypeCell colSpan={3} recordId={recordId} requirement={requirement} />
            <ExpirationDateCell colSpan={1} document={requirement.documents[0]} />
            <FileNameCell colSpan={3} document={requirement.documents[0]} recordId={recordId} />
            <UploadDateCell colSpan={1} document={requirement.documents[0]} />
            <StatusCell colSpan={2} document={requirement.documents[0]} insuredId={parseInt(insuredId, 10)} recordId={recordId} requirement={requirement} />
          </Row>
          {
            requirement.documents.slice(1).map(document => (
              <Row data-cy={CypressIds.documentRow(document?.id)} key={document?.id}>
                <EmptyCell colSpan={3} />
                <ExpirationDateCell colSpan={1} document={document} />
                <FileNameCell colSpan={3} document={document} recordId={recordId} />
                <UploadDateCell colSpan={1} document={document} />
                <EmptyCell colSpan={2} />
              </Row>
            ))
          }
        </Box>
      ))}
    </VStack>
  );
};

DocumentsTable.propTypes = {
  insuredId: PropTypes.number.isRequired,
  recordId: PropTypes.number.isRequired,
  jobId: PropTypes.number.isRequired
};

export default DocumentsTable;
